import { Flex } from "antd";
import dayjs from "dayjs";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    getMarketInfoCollectionThunk,
    getStorageInventoryGetThunk,
} from "tm-shared/api";
import { OrderType, ProductOrderItemDetails, UserRole } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import BaseButton from "@/components/BaseButton";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import { MarketCard } from "@/components/MarketCard";
import Meta from "@/components/Meta";
import { DataConfirmModal } from "@/components/Modals/DataConfirmModal";
import { TotalSum } from "@/components/TotalSum";
import { PrimaryText } from "@/components/Typography/Styled";
import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { useSaveOrder } from "@/pages/Checkout/hooks/indes";

import { ProductRow } from "./components/ProductRow";
import {
    StyledODHeader,
    StyledODTable,
    StyledODTableBody,
    StyledODTableHeader,
    StyledODTableHeaderItem,
} from "./Styled";

export const ORDER_TYPES_WITH_DISABLED_CHANGES = [OrderType.P, OrderType.R];

export function OrderDetailsPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const { orderId } = useParams<{ orderId: string }>();
    const { handleSaveOrder } = useSaveOrder();

    const { role } = useAppSelector((state) => state.user);
    const { currentUser } = useAppSelector((state) => state.sv);

    const correctPath =
        role === UserRole.Sv ? `/sv-checkout/${currentUser}` : "/checkout";

    const { checkoutList, checkoutCompletedList, markets } = useAppSelector(
        (state) => state.work,
    );
    const { inventoryList, offlineMode, ignoreRemainingsOrders } =
        useAppSelector((state) => state.offline);

    const [validationErrors, setValidationErrors] = useState<
        Record<string, boolean>
    >({});

    const [confirmModal, setConfirmModal] = useState(false);

    const isOrderClosed = useMemo(
        () => searchParams.get("isClosed")?.split("-")?.[0] === "true",
        [searchParams],
    );

    const order = useMemo(
        () =>
            isOrderClosed
                ? checkoutCompletedList?.[orderId]
                : checkoutList?.[orderId],
        [orderId, isOrderClosed, checkoutList, checkoutCompletedList],
    );

    const [localProducts, setLocalProducts] = useState<
        Record<string, ProductOrderItemDetails>
    >(() => order?.products ?? {});

    const market = useMemo(
        () => markets?.[order?.marketId],
        [markets, order?.marketId],
    );

    const currentInventoryList = inventoryList?.[market?.storage?.id];

    const totalOrderPrice = useMemo(() => {
        return Object.values(localProducts).reduce((sum, product) => {
            return sum + product.price * product.count;
        }, 0);
    }, [localProducts]);

    const hasChanges = useMemo(
        () => !isEqual(localProducts, order?.products ?? {}),
        [localProducts, order?.products],
    );

    const validateProductCount = useCallback(
        (barcode: string, count: number) => {
            const maxCount = ignoreRemainingsOrders[orderId]?.includes(barcode)
                ? Number.MAX_SAFE_INTEGER
                : currentInventoryList?.[barcode] || 0;

            const isValid = count <= maxCount;
            setValidationErrors((prev) => ({ ...prev, [barcode]: !isValid }));
            return isValid;
        },
        [orderId, currentInventoryList, ignoreRemainingsOrders],
    );

    const handleProductCountChange = useCallback(
        (item: ProductOrderItemDetails, newCount: number | null) => {
            const count = newCount ?? 0;
            if (validateProductCount(item.barcode, count)) {
                setLocalProducts((prev) => {
                    return {
                        ...prev,
                        [item.barcode]: {
                            ...prev[item.barcode],
                            count,
                        },
                    };
                });
            }
        },
        [validateProductCount],
    );

    const handleProductDelete = useCallback((barcode: string) => {
        setLocalProducts((prev) => {
            const updated = { ...prev };
            delete updated[barcode];
            return updated;
        });
    }, []);

    const [onSaveOrder, saveLoading] = useAsyncAction(async () => {
        await handleSaveOrder({
            orderId,
            products: localProducts,
            storageId: market?.storage?.id ?? "",
        });

        navigate(
            role === UserRole.Sv
                ? `/sv-checkout/${currentUser || ""}`
                : "/checkout",
        );
    });

    useEffect(() => {
        if (!order?.marketId || offlineMode) return;

        if (!markets?.[order.marketId]) {
            dispatch(
                getMarketInfoCollectionThunk({
                    marketIds: [order.marketId],
                }),
            );
        }

        const market = markets?.[order.marketId];
        if (market?.partner) {
            dispatch(
                getStorageInventoryGetThunk({
                    storageId: market.storage.id,
                }),
            );
        }
    }, [dispatch, markets, order?.marketId, offlineMode]);

    const isDisabledType = ORDER_TYPES_WITH_DISABLED_CHANGES.includes(
        order?.orderType,
    );

    const editable = !isOrderClosed && !isDisabledType;

    const visibleProducts = useMemo(
        () => Object.values(localProducts),
        [localProducts],
    );

    const handleAdditionalSale = useCallback(() => {
        if (hasChanges) {
            setConfirmModal(true);
            return;
        }

        navigate(
            buildRoutePath(ROUTES.CheckoutAddProducts, {
                marketId: order.marketId,
                orderId,
                isCheckout: "checkout",
            }),
        );
    }, [hasChanges, navigate, order.marketId, orderId]);

    if (!order) return <div>Замовлення не знайдено</div>;

    return (
        <>
            <Meta title="Деталі замовлення" />

            <Header
                title="Деталі замовлення"
                path={correctPath}
                needConfirm={hasChanges}
            />

            {totalOrderPrice !== 0 && !isDisabledType && (
                <TotalSum totalSum={totalOrderPrice} />
            )}

            <MainContainer>
                <StyledODHeader>
                    <MarketCard
                        address={
                            markets?.[order.marketId]?.address?.name || "-"
                        }
                        fop={markets?.[order.marketId]?.contragent?.name || "-"}
                    />
                    <PrimaryText style={{ textWrap: "nowrap" }}>
                        {dayjs().format("DD.MM.YY")}
                    </PrimaryText>
                </StyledODHeader>

                <StyledODTable>
                    <StyledODTableHeader>
                        <StyledODTableHeaderItem>Ціна</StyledODTableHeaderItem>
                        <StyledODTableHeaderItem>К-сть</StyledODTableHeaderItem>
                        <StyledODTableHeaderItem>Сума</StyledODTableHeaderItem>
                    </StyledODTableHeader>

                    <StyledODTableBody>
                        {visibleProducts.map((item, index) => (
                            <ProductRow
                                key={`${item.barcode}-${index}`}
                                barcode={item.barcode}
                                orderId={orderId}
                                item={item}
                                isClosed={isOrderClosed}
                                ignoreRemainingsOrders={ignoreRemainingsOrders}
                                currentInventoryList={currentInventoryList}
                                validationErrors={validationErrors}
                                onCountChange={handleProductCountChange}
                                onInputBlur={() =>
                                    setValidationErrors((prev) => ({
                                        ...prev,
                                        [item.barcode]: false,
                                    }))
                                }
                                onDelete={handleProductDelete}
                                orderType={order?.orderType}
                                loading={false}
                            />
                        ))}
                    </StyledODTableBody>
                </StyledODTable>

                {editable && (
                    <Flex vertical gap={8}>
                        <BaseButton
                            type="default"
                            onClick={handleAdditionalSale}
                            disabled={saveLoading}
                        >
                            Додатковий продаж
                        </BaseButton>

                        <BaseButton
                            type="primary"
                            onClick={onSaveOrder}
                            disabled={!hasChanges}
                            loading={saveLoading}
                        >
                            Зберегти зміни
                        </BaseButton>
                    </Flex>
                )}
            </MainContainer>

            {confirmModal && hasChanges && (
                <DataConfirmModal
                    onClickBack={() =>
                        navigate(
                            buildRoutePath(ROUTES.CheckoutAddProducts, {
                                marketId: order.marketId,
                                orderId,
                                isCheckout: "checkout",
                            }),
                        )
                    }
                    onClose={() => setConfirmModal(false)}
                    infoText="Введені дані не будуть збережені. Ви впевнені, що хочете перейти на додатковий продаж?"
                    backTextButton="Перейти на додатковий продаж"
                />
            )}
        </>
    );
}

export default OrderDetailsPage;
